import { CaptureProfileDTO } from 'business/models/Models';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FmlxRadioButton } from 'fmlx-common-ui';
import { useRMSelector } from 'redux/hooks';
import { selectExp } from 'redux/selectors/ExperimentStateSelector';
import { IStyle } from 'Styles';
import { getAlertTypeByResponseType, showAlertSnackBar, UpdateArray } from 'utils/Global';
import { ImageNowSidePanelProps } from './ImageNowSidePanel';
import { CustomCheckBox } from 'components/input';
import CaptureProfileRendrer from './CaptureProfileRenderer';
import './ImageNow.scss';
import ImageNowService from 'business/services/ImageNowService';
import CaptureProfileService from 'business/services/CaptureProfileService';
import {
  useAllWellNavigationDetails,
  useSelectedDropImageParameters,
} from 'redux/hooks/experiment';
import { useModalHandlers } from 'components/modal';
import {
  useExperimentImagingDisabledState,
  useIsWellImagingEnabled,
  usePlateImagingState,
} from 'components/Experiment/hooks';
import ETreeNodeAccessRightNames from 'enums/ETreeNodeAccessRightNames';
import ESnackBarPosition from 'enums/ESnackBarPosition';

enum imagingLevelEnum {
  drop = 0,
  plate = 1,
  allPlates = 2,
}

export function RadioButton({ onClick, selected, disabled = false, items }) {
  return (
    <FmlxRadioButton
      id="commandpanel-imagenow-radiobutton"
      selectedIndex={selected}
      items={items}
      disabled={disabled}
      onChange={onClick}
      alignment="left"
    />
  );
}

const selectedCaptures = [];
function ImageNow({
  isOpenInPopup = false,
  onClose,
  expIds = [],
}: ImageNowSidePanelProps) {
  const selectedDropImageParameters = useSelectedDropImageParameters();
  const { cpId, dropNumber, regionName } = selectedDropImageParameters || {};
  const [captures, setCaptures] = useState<CaptureProfileDTO>(null);
  const [checkedState, setCheckedState] = useState([]);
  const [selectedLevel, setSelectedLevel] = useState(
    isOpenInPopup ? imagingLevelEnum.allPlates : imagingLevelEnum.plate,
  );
  const [intrestingDrop, setIntrestingDrop] = useState(true);
  const [roi, setRoi] = useState(false);
  const [disable, setdisable] = useState(
    selectedCaptures.length < 1 && (intrestingDrop || roi),
  );
  const exp = useRMSelector(selectExp);
  const selectedWellDetails = useAllWellNavigationDetails();
  const experimentImagingDisabled = useExperimentImagingDisabledState();
  const isWellImagingEnabled = useIsWellImagingEnabled(dropNumber);
  const plateImagingEnabled = usePlateImagingState();

  const selectedPlateIds = selectedWellDetails?.map((w) => w.plateID);
  const { showModal, hideModal } = useModalHandlers();

  const wells = useMemo(() => {
    if (!exp || !exp.plateDto) {
      return null;
    }

    const plates = exp.plateDto.filter((pl) => selectedPlateIds.includes(pl.plateId));

    const _wells = [];
    plates.forEach((plate) =>
      plate.ingredients
        ? plate.ingredients.wellList.filter((el) => {
            if (
              selectedWellDetails.some(
                (it) => it.wellNum == el.wellNumber && it.plateID == plate.plateId,
              )
            ) {
              _wells.push(el);
            }
          })
        : null,
    );
    return _wells;
  }, [exp?.plateDto, selectedWellDetails]);

  const wellDropsId = useMemo(
    () =>
      wells?.map(
        (item) => item.dropList.find((drop) => drop.dropNumber == dropNumber)?.dropId,
      ),
    [wells, dropNumber],
  );
  const handleROI = useCallback((e) => {
    const { checked } = e.target;
    setRoi(checked);
  }, []);
  const handleIntrestingDrop = useCallback((e) => {
    const { checked } = e.target;
    setIntrestingDrop(checked);
  }, []);

  const levelHandler = useCallback((item) => {
    if (item.index === imagingLevelEnum.drop) {
      setIntrestingDrop(false);
      setRoi(false);
    }
    setSelectedLevel(item.index);
  }, []);

  const captureHandler = useCallback(
    (position, id) => {
      const updatedCheckedState = checkedState.map((item, index) => {
        const status = index === position;
        if (status) {
          UpdateArray(selectedCaptures, id, !item, setdisable);
        }
        return status ? !item : item;
      });

      setCheckedState(updatedCheckedState);
    },
    [checkedState],
  );

  useEffect(() => {
    selectedCaptures.length = 0;
    if (captures) {
      const index = captures.captureProfileRows.findIndex((cp) => cp.id == cpId);
      if (index >= 0) {
        captureHandler(index, cpId);
      }
    }
  }, [captures]);
  useEffect(() => {
    if (captures == null) {
      fetchData().then((cps) => {
        setCheckedState(new Array(cps.captureProfileRows.length).fill(false));
        setCaptures(cps);
      });
    }

    async function fetchData(): Promise<CaptureProfileDTO> {
      const captureProfileService = new CaptureProfileService();
      return await captureProfileService.getCaptureProfile();
    }
  }, []);

  const showImagingDisabledMessage = (message: string) => {
    showModal({
      body: <>{message}</>,
      heading: 'Imaging Disabled',
      onOk: hideModal,
      isOkOnly: true,
      okButtonText: 'OK',
    });
  };

  const ImageNowClickHandler = async (selCaptures) => {
    if (experimentImagingDisabled) {
      showImagingDisabledMessage('Experiment imaging is disabled.');
      return;
    }
    if (selectedLevel === imagingLevelEnum.plate && !plateImagingEnabled) {
      showImagingDisabledMessage('Plate imaging is disabled.');
      return;
    }
    if (selectedLevel === imagingLevelEnum.drop && !isWellImagingEnabled) {
      showImagingDisabledMessage('Well imaging is disabled.');
      return;
    }
    const selectedSubLevelsArray = [];
    if (intrestingDrop == true) {
      selectedSubLevelsArray.push('intresting');
    }
    if (roi == true) {
      selectedSubLevelsArray.push('ROI');
    }

    const imageNowService = new ImageNowService();
    const response = await imageNowService.ImageNow(
      imagingLevelEnum[selectedLevel],
      selectedSubLevelsArray,
      selCaptures,
      exp ? [exp.experimentId] : expIds,
      [...(selectedPlateIds?.length ? selectedPlateIds : [])],
      [...(wellDropsId?.length ? wellDropsId : [])],
      regionName || 'Drop',
    );
    if (!response) {
      return null;
    }
    if (onClose) {
      onClose();
    }
    showAlertSnackBar(
      getAlertTypeByResponseType(response?.responseType),
      ESnackBarPosition.TOP,
      response?.message,
    );
    return response;
  };

  return (
    <div id="commandpanel-imagenow-container" style={styles.mainContainer}>
      <div id="commandpanel-imagenow-contentcontainer" style={styles.contentContainer}>
        <div id="commandpanel-imagenow-desctext" style={styles.DescTextStyle}>
          Please select affected level of imaging and its imaging settings
        </div>
        <div id="commandpanel-imagenow-level-section" style={styles.subSectionContainer}>
          <label id="commandpanel-imagenow-level-label" style={styles.labelStyle}>
            {' '}
            Level
          </label>
          <div id="commandpanel-imagenow-level-content" style={styles.subSectionContent}>
            <RadioButton
              selected={selectedLevel}
              onClick={levelHandler}
              disabled={isOpenInPopup}
              items={[
                {
                  content: 'Selected Drop',
                  hint: '',
                  type: FmlxRadioButton.HintType.INFORMATION,
                  disabled: isOpenInPopup,
                },
                {
                  content: 'Current Plate',
                  hint: '',
                  type: FmlxRadioButton.HintType.INFORMATION,
                  disabled: isOpenInPopup,
                },
                {
                  content: 'All Plate(s)',
                  hint: '',
                  type: FmlxRadioButton.HintType.INFORMATION,
                  disabled: false,
                },
              ]}
            />
          </div>
        </div>

        <div
          id="commandpanel-imagenow-sublevel-section"
          style={styles.subSectionContainer}
        >
          <label id="commandpanel-imagenow-sublevel-label" style={styles.labelStyle}>
            {' '}
            Sub Level
          </label>
          <div
            id="commandpanel-imagenow-sublevel-content"
            style={styles.subSectionContent}
          >
            <CustomCheckBox
              id="commandpanel-imagenow-checkbox-intrestingdrop"
              onChange={handleIntrestingDrop}
              checked={intrestingDrop}
              value="intrestingDrop"
              disabled={selectedLevel === imagingLevelEnum.drop}
              label="Interesting Drop(s)"
              mainContainerStyle={styles.checkbox}
              labelStyle={styles.labelStyle1}
            />

            <CustomCheckBox
              id="commandpanel-imagenow-checkbox-roi"
              onChange={handleROI}
              checked={roi}
              value="roi"
              disabled={selectedLevel === imagingLevelEnum.drop}
              label="Region of Interest(s)"
              mainContainerStyle={styles.checkbox}
              labelStyle={styles.labelStyle1}
            />
          </div>
        </div>

        <div
          id="commandpanel-imagenow-imagingsettings-section"
          style={{
            ...styles.subSectionContainer,
            height: isOpenInPopup ? '25vh' : '45vh',
          }}
        >
          <label
            id="commandpanel-imagenow-imagingsettings-label"
            style={styles.labelStyle}
          >
            {' '}
            Imaging Settings
          </label>
          <div
            id="commandpanel-imagenow-imagingsettings-content"
            style={{
              ...styles.subSectionContent,
              overflowY: 'auto',
            }}
          >
            <CaptureProfileRendrer
              captures={captures}
              checkedState={checkedState}
              captureHandler={captureHandler}
              showInTable={false}
            />
          </div>
        </div>
      </div>
      <div id="commandpanel-imagenow-actioncontainer" style={styles.actionContainer}>
        <button
          id="commandpanel-imagenow-startimaging-button"
          type="button"
          style={{ ...styles.buttonStyle, opacity: disable ? 0.5 : 1 }}
          className="disabled:opacity-70 mr-8"
          data-dismiss="modal"
          onClick={() => ImageNowClickHandler(selectedCaptures)}
          disabled={
            disable ||
            (exp?.accessRightName &&
              exp?.accessRightName !== ETreeNodeAccessRightNames.Modify)
          }
        >
          Start Imaging
        </button>
      </div>
    </div>
  );
}

const styles: IStyle = {
  labelStyle1: {
    fontFamily: 'Arial',
    fontWeight: 400,
    fontSize: '1rem',
    color: '#4F4F4F',
    marginLeft: '0.75rem',
  },
  checkbox: {
    marginBottom: 8,
  },
  DescTextStyle: {
    fontSize: '1rem',
    fontFamily: 'Roboto',
    fontWeight: 400,
    marginTop: 10,
  },
  mainContainer: {
    display: 'flex',
    height: 'inherit',
    flexDirection: 'column',
  },
  contentContainer: {
    padding: '0px 25px',
    height: '85%',
    width: '100%',
  },
  actionContainer: {
    display: 'flex',
    alignSelf: 'flex-end',
    alignItems: 'center',
    height: '15%',
    marginRight: 10,
  },
  subSectionContainer: {
    display: 'flex',
    flexDirection: 'column',
  },
  labelStyle: {
    fontFamily: 'Arial',
    fontWeight: 700,
    fontSize: '1.2rem',
    color: '#000000',
  },
  subSectionContent: {
    paddingLeft: '10%',
  },
  optionLabelStyle: {
    fontFamily: 'Arial',
    fontWeight: 400,
    fontSize: '1rem',
    color: '#4F4F4F',
    marginLeft: '0.75rem',
  },
  buttonStyle: {
    margin: '5px',
    border: 'none',
    width: '198px',
    background: '#F89723',
    borderRadius: 4,
    fontSize: '1rem',
    lineHeight: '21px',
    display: 'flex',
    alignItems: 'center',
    textAlign: 'center',
    color: '#ffffff',
    height: 44,
    justifyContent: 'center',
    textTransform: 'uppercase',
  },
};

export default ImageNow;
